/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, EventEmitter, forwardRef, input, Input, Output } from '@angular/core';
import { AbstractContactVO } from '@well/domain';
import { BpSelectableContact } from './bp-selectable-contact.model';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'well-bp-selectable-contact-list',
  host: { class: 'irian-custom-cva' },
  templateUrl: './bp-selectable-contact-list.component.html',
  styleUrls: ['./bp-selectable-contact-list.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BpSelectableContactListComponent),
      multi: true,
    }
  ],
})
export class BpSelectableContactListComponent implements ControlValueAccessor {
  get selectedContactsUuids(): string[] {
    return this._selectedContactsUuids;
  }

  @Input() set selectedContactsUuids(value: string[]) {
    this._selectedContactsUuids = value;

    if (this._selectedContactsUuids && this._selectedContactsUuids.length && this._contactList.length) {
      this.selectBpContacts();
    }
  }

  private _contactList: AbstractContactVO[] = [];
  private selectedContacts: AbstractContactVO[] = [];

  @Input() set contactList(value: AbstractContactVO[]) {
    if (value && value.length) {
      this._contactList = value;
      const companyNames: Set<string> = new Set<string>();
      this._contactList.forEach((v) => companyNames.add(v.companyName));
      if (companyNames.size > 1) {
        this.showCompanyForContact = true;
      }

      this.bpSelectableContact = this._contactList.map((contact) => {
        if (this._selectedContactsUuids) {
          const selected = !!this._selectedContactsUuids.find((uuid) => uuid === contact.uuid);
          return new BpSelectableContact(contact, selected);
        } else {
          return new BpSelectableContact(contact, false);
        }
      });
      this.bpSelectedContacts = this.bpSelectableContact.filter((bpContact) => bpContact.selected);
    }
  }

  get contactList(): AbstractContactVO[] {
    return this._contactList;
  }

  @Input() viewOnly = false;
  @Input() disablePreviouslySelected = false;
  @Input() isContactListExpanded = false;
  @Input() showCompanyForContact = false;
  displayCheckbox = input(true);
  singleSelect = input(false);
  publicContacts = input<boolean[]>([]);

  private _selectedContactsUuids: string[] = [];
  @Output() selectedContactsUuidsChange: EventEmitter<string[]> = new EventEmitter<string[]>();

  @Output() submitContacts: EventEmitter<string[]> = new EventEmitter<string[]>();

  bpSelectedContacts: BpSelectableContact[] = [];

  bpSelectableContact: BpSelectableContact[] = [];

  private onChangeCallback: any | undefined = undefined;
  private onTouchedCallback: any | undefined = undefined;
  disabled: boolean | undefined = undefined;

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(selectedContacts: AbstractContactVO[]): void {
    if (selectedContacts && Array.isArray(selectedContacts)) {
      this.selectedContactsUuids = selectedContacts.map((item) => item.uuid);
      this.selectedContacts = selectedContacts;
    }
  }

  onContactSelectionChange(event: boolean, contact: BpSelectableContact) {
    if (this.singleSelect() && event) {
      this.clearOtherSelections(contact);
    }

    if (!event) {
      this.removeContactFromSelectedList(contact);
    } else {
      this.addContactToSelectedList(contact);
    }

    this.selectedContactsUuidsChange.emit(this._selectedContactsUuids);
    if (this.onChangeCallback) {
      this.onChangeCallback(this.selectedContacts);
    }
    this.onTouchedCallback?.();
  }

  private clearOtherSelections(selectedContact: BpSelectableContact) {
    this.bpSelectableContact.forEach((bpContact) => {
      if (bpContact !== selectedContact && bpContact.selected) {
        bpContact.selected = false;
        this.removeContactFromSelectedList(bpContact);
      }
    });
  }

  private removeContactFromSelectedList(removedContact: BpSelectableContact) {
    const removeIndex = this.bpSelectedContacts.indexOf(removedContact);
    this.bpSelectedContacts.splice(removeIndex, 1);
    this._selectedContactsUuids = this._selectedContactsUuids.filter((uuid) => uuid !== removedContact.contact.uuid);
    this.selectedContacts = this.selectedContacts.filter((contact) => contact.uuid !== removedContact.contact.uuid);
  }

  private addContactToSelectedList(addedContact: BpSelectableContact) {
    this.bpSelectedContacts.push(addedContact);
    if (!this._selectedContactsUuids) {
      this._selectedContactsUuids = [];
      this.selectedContacts = [];
    }
    this._selectedContactsUuids.push(addedContact.contact.uuid);
    this.selectedContacts.push(addedContact.contact);
  }

  private selectBpContacts() {
    this.bpSelectableContact = [];
    this.bpSelectableContact = this._contactList.map((contact) => {
      const selected = !!this._selectedContactsUuids.find((uuid) => uuid === contact.uuid);
      return new BpSelectableContact(contact, selected);
    });
    this.bpSelectedContacts = this.bpSelectableContact.filter((bpContact) => bpContact.selected);
  }

  trackBy(index: any, bpContact: any) {
    return bpContact[bpContact.uuid];
  }
}
